/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Fab,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Paper,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import separateNumberFormat from 'src/utils/separateNumberFormat';
import {
  BillableMetric,
  PlanSimulationResponse,
  PlanSimulationV2Params,
  UserPackagePlan
} from 'src/models/register';
import { useLazyQuery } from '@apollo/client';
import {
  GET_BILLABLE_METRICS,
  GET_PLAN_SIMULATION_V2
} from 'src/services/graphql/register/query';
import { useAlert } from 'src/hooks/useAlert';
import {
  reducerUpdatePackagePlan,
  reducerUpdatePrices,
  reducerUpdateSimulationPlanItems,
  reducerUpdateSimulationPromoCode
  // reducerUpdateSimulationReferralCode
} from 'src/features/register';
import { Add, Cancel, Remove } from '@mui/icons-material';
import ChooseYourPlan from 'src/content/settings/Billing/ChooseYourPlan';
import CloseIcon from '@mui/icons-material/Close';
import { themeColors } from 'src/theme/schemes/PureLightTheme';
import useRegister from 'src/services/useRegister';
import { UserSubscriptionPackagePlan } from 'src/services/useBillingSystem';

interface Props {
  periodes: Array<any>;
  setPeriodes: any;
}

const LiteTypography = styled(Typography)(
  ({ theme }) => `
    background: ${theme.colors.gradients.blue1};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    `
);

const ButtonPeriod = styled(Button)(
  ({ theme }) => `
       padding: ${theme.spacing(1)};
       font-size: 12px;
       background: ${theme.colors.gradients.blue1};
       &:hover {
        background: ${theme.colors.gradients.blue1};
       };
    `
);

const ButtonIcon = styled(Fab)(
  ({ theme }) => `
    background: ${theme.colors.gradients.blue1};
    color: ${theme.palette.common.white};
    &:hover {
        background: ${theme.colors.gradients.blue2};
    }
      `
);

interface ModalPlansProps {
  open: boolean;
  handleClose: () => void;
  handleClickPlan: (plan: UserPackagePlan, periodValue: string) => void;
  isFreeTrial: boolean;
}

const ModalPlans = ({
  open,
  handleClose,
  handleClickPlan,
  isFreeTrial
}: ModalPlansProps) => {
  const theme = useTheme();

  return (
    <Dialog onClose={handleClose} open={open} fullWidth maxWidth={'lg'}>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          m: { xs: 0, md: theme.spacing(1, 3) }
        }}
      >
        <Stack gap={2}>
          <ChooseYourPlan
            title="Plans That Scale With Your Need"
            hideClickBack
            onClickPlan={handleClickPlan}
            hideCTAButtons={isFreeTrial}
          />
          {/* <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            size="large"
            sx={{ px: 6, mx: 'auto' }}
          >
            Done
          </Button> */}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

interface BillableMetricsWithQuota extends BillableMetric {
  quota: number;
}

const PricingCalculator: React.FC<Props> = ({ periodes, setPeriodes }) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const isFreeTrial =
    Boolean(params.get('isfreetrial')) && params.get('isfreetrial') === 'true';
  const promoCodeUrl = params.get('promocode');
  // const referalCodeUrl = params.get('referralcode');
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const planParam = params.get('plan') ?? 'starter';
  const planCurrency = params.get('currency') ?? 'idr';
  const { handleClickAlert } = useAlert();
  const [billableMetricsWithQuota, setbillableMetricsWithQuota] = useState<
    BillableMetricsWithQuota[] | null
  >(null);
  const [promoCodes, setPromoCodes] = useState<string[]>(
    promoCodeUrl ? [promoCodeUrl] : []
  );
  const [promoCodeInput, setPromoCodeInput] = useState<string>('');
  const [isPromoError, setIsPromoError] = useState<boolean>(false);
  // const [isPromoSubmitted, setIsPromoSubmitted] = useState<boolean>(false);
  const [promoCodeErrorMessage, setPromoCodeErrorMessage] =
    useState<string>('');

  // const [referalCode, setReferalCode] = useState<string>(
  //   referalCodeUrl ? referalCodeUrl : ''
  // );
  // const [isReferalError, setIsReferalError] = useState<boolean>(false);
  // const [isReferalSubmitted, setIsReferalSubmitted] = useState<boolean>(false);
  // const [referalCodeErrorMessage, setReferalCodeErrorMessage] =
  //   useState<string>('');

  const [openModalPlans, setOpenModalPlans] = useState<boolean>(false);

  const [isAgreementChecked, setIsAgreemenChecked] = useState<boolean>(false);

  const {
    userPackagePlan,
    email,
    planSimulationV2: {
      referralCode,
      discount,
      referralDiscount,
      subTotal,
      total,
      promoType
      // planItems
    }
    // packagePlan
  } = useAppSelector((state) => state.register);
  const { getUserPackagePlan } = useRegister();

  const getQuota = (metricName: string, plan: UserPackagePlan) => {
    if (!plan) return null;
    if (metricName === 'SOCIAL_PROFILE') return plan.basicObjectQuota;
    if (metricName === 'KEYWORD_HASHTAG') return plan.advanceObjectQuota;
    if (metricName === 'CLIPPING') return plan.onlineMediaObjectQuota;
    if (metricName === 'ENTITY') return plan.entityQuota;
    return null;
  };

  // const allDefaultPromoCode = promoCodesByPeriod
  //   .map((item) => item.promoCode)
  //   .flat()
  //   .map((value) => value.code);

  const getPlanSimulationVariables = (
    metrics: BillableMetricsWithQuota[] | null,
    plans: UserPackagePlan[],
    promoCodesList: string[],
    period: number
  ) => {
    const currentPlan = getCurrentPlan(metrics, plans);
    const isCustomPlan = currentPlan.name === 'custom';

    return {
      countryId: initCountryId,
      period,
      promoCodes: promoCodesList,
      planItems: isCustomPlan
        ? metrics?.map((metric) => ({
            billableMetricId: metric.id,
            quota: metric.quota
          })) ?? null
        : null,
      email,
      referalCode: referralCode,
      packagePlanId: !isCustomPlan ? currentPlan.id : null
    };
  };

  // const handleResetReferralCode = () => {
  //   const isCustomPlan = packagePlan.name === 'custom';

  //   setReferalCode('');
  //   setIsReferalError(false);
  //   setIsReferalSubmitted(false);
  //   getPlanSimulationV2({
  //     variables: {
  //       planItems: isCustomPlan ? planItems : null,
  //       countryId: initCountryId,
  //       period: selectedPeriod.value,
  //       promoCodes: promoCodes,
  //       email,
  //       packagePlanId: !isCustomPlan ? packagePlan.id : null,
  //       referalCode: ''
  //     }
  //   });
  // };

  // const handleUseReferral = (referral: string) => {
  //   const isCustomPlan = packagePlan.name === 'custom';

  //   setIsReferalSubmitted(true);
  //   getPlanSimulationV2({
  //     variables: {
  //       planItems: isCustomPlan ? planItems : null,
  //       countryId: initCountryId,
  //       period: selectedPeriod.value,
  //       promoCodes: promoCodes,
  //       email,
  //       referalCode: referral,
  //       packagePlanId: !isCustomPlan ? packagePlan.id : null
  //     }
  //   });
  // };

  const [
    getBillableMetrics,
    {
      data: billableMetrics,
      loading: billableMetricsLoading,
      error: billableMetricsError
    }
  ] = useLazyQuery<{ billing_getBillableMetrics: BillableMetric[] }>(
    GET_BILLABLE_METRICS,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      onCompleted({ billing_getBillableMetrics }) {
        // get initial metric quota
        const dataWithQuota: BillableMetricsWithQuota[] =
          billing_getBillableMetrics.map((metric) => {
            const plan = userPackagePlan.data.find((plan) =>
              isFreeTrial ? plan.name === 'starter' : plan.name === planParam
            );
            if (getQuota(metric.code, plan) !== null) {
              return {
                ...metric,
                quota: getQuota(metric.code, plan)
              };
            }
          });
        const filteredDataWithQuota = dataWithQuota.filter((item) => !!item);

        getPlanSimulationV2({
          variables: getPlanSimulationVariables(
            filteredDataWithQuota,
            userPackagePlan.data,
            promoCodes,
            selectedPeriod.value
          )
        });
        setbillableMetricsWithQuota(filteredDataWithQuota);
      }
    }
  );

  const initCountryId =
    planCurrency === 'idr'
      ? '9cd88e16-510e-49f0-9ce8-f4d27f0b85bb'
      : '162021c9-becc-11eb-a7ce-1e87ea3f1b00';

  const selectedPeriod = useMemo(
    () => periodes.find((period) => period.isActive),
    [periodes]
  );

  const [getPlanSimulationV2, { loading: planSimulationLoading }] =
    useLazyQuery<
      {
        billing_getPlanSimulationV2: PlanSimulationResponse;
      },
      PlanSimulationV2Params
    >(GET_PLAN_SIMULATION_V2, {
      onCompleted: (data) => {
        if (!data) return;
        if (
          data.billing_getPlanSimulationV2.isPromoValid === false &&
          data.billing_getPlanSimulationV2.promoCodes.length > 0
        ) {
          setIsPromoError(true);
          setPromoCodeErrorMessage(
            data.billing_getPlanSimulationV2.promoErrorMessage[0]
          );
          const updatedPromoCodes = promoCodes.filter(
            (item) => item !== promoCodeInput.toUpperCase()
          );
          setPromoCodes(updatedPromoCodes);
        } else {
          setIsPromoError(false);
          setPromoCodeInput('');
          dispatch(
            reducerUpdateSimulationPromoCode(
              data.billing_getPlanSimulationV2.promoCodes
            )
          );
        }

        // if (
        //   data.billing_getPlanSimulationV2.isReferralValid === false &&
        //   data.billing_getPlanSimulationV2.referralCode
        // ) {
        //   setIsReferalError(true);
        //   setReferalCodeErrorMessage(
        //     data.billing_getPlanSimulationV2.referralErrorMessages[0]
        //   );
        // } else {
        //   setIsReferalError(false);
        //   dispatch(
        //     reducerUpdateSimulationReferralCode(
        //       data.billing_getPlanSimulationV2.referralCode
        //     )
        //   );
        //   if (data.billing_getPlanSimulationV2.referralCode) {
        //     handleClickAlert({
        //       horizontal: 'center',
        //       vertical: 'top',
        //       message: 'Referral Code Applied',
        //       severity: 'success'
        //     });
        //   }
        // }

        dispatch(
          reducerUpdateSimulationPlanItems(
            data.billing_getPlanSimulationV2.planItems
          )
        );
        dispatch(
          reducerUpdatePrices({
            discount: data?.billing_getPlanSimulationV2?.discount,
            referralDiscount:
              data?.billing_getPlanSimulationV2?.referralDiscount,
            total: data?.billing_getPlanSimulationV2?.total,
            subTotal: data?.billing_getPlanSimulationV2?.subTotal,
            promoType: data?.billing_getPlanSimulationV2?.promoType
          })
        );
        // Handle package promo type
        if (data.billing_getPlanSimulationV2.promoType === 'package') {
          const updatedMetrics = billableMetricsWithQuota.map((metric) => {
            const matchedMetric =
              data.billing_getPlanSimulationV2.planItems.find(
                (plan) => plan.billableMetricId === metric.id
              );
            return { ...metric, quota: matchedMetric.quota };
          });
          setbillableMetricsWithQuota(updatedMetrics);
        }
      },
      onError: () => {
        setIsPromoError(true);
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Error fetching price, please try again.',
          severity: 'error'
        });
      },
      // Disable cache for this query to always get fresh data
      fetchPolicy: 'network-only'
    });

  const handleChangePeriod = (id: string) => {
    setPeriodes((prevPeriodes) =>
      prevPeriodes.map((period) => {
        if (period.id === id) return { ...period, isActive: true };
        return { ...period, isActive: false };
      })
    );
  };

  const getCurrentPlan = (
    state: BillableMetricsWithQuota[] | null,
    plans: UserPackagePlan[]
  ): UserSubscriptionPackagePlan => {
    if (!state) return { id: '', name: '', displayName: '' };
    const getSocialProfile = state.find(
      (item) => item.code === 'SOCIAL_PROFILE'
    );
    const getKeywordHashtag = state.find(
      (item) => item.code === 'KEYWORD_HASHTAG'
    );
    const getClipping = state.find((item) => item.code === 'CLIPPING');
    const getEntity = state.find((item) => item.code === 'ENTITY');
    const getPackage = plans.find(
      (item) =>
        item.basicObjectQuota === getSocialProfile.quota &&
        item.advanceObjectQuota === getKeywordHashtag.quota &&
        item.onlineMediaObjectQuota === getClipping.quota &&
        item.entityQuota === getEntity.quota
    );
    if (getPackage) {
      return {
        id: getPackage.id,
        displayName:
          getPackage.name.charAt(0).toUpperCase() + getPackage.name.slice(1),
        name: getPackage.name
      };
    }

    // if custom plan
    const customPlanId = plans.find((item) => item.name === 'custom')?.id;
    return {
      id: customPlanId ?? '',
      name: 'custom',
      displayName: 'Custom'
    };
  };

  const handleUsePromo = (promoCodeValue: string) => {
    // setIsPromoSubmitted(true);
    const isAlreadyExist = promoCodes.includes(promoCodeValue.toUpperCase());

    if (!isAlreadyExist) {
      const updatedPromoCodes = [...promoCodes, promoCodeValue.toUpperCase()];
      setPromoCodes(updatedPromoCodes);

      getPlanSimulationV2({
        variables: getPlanSimulationVariables(
          billableMetricsWithQuota,
          userPackagePlan.data,
          updatedPromoCodes,
          selectedPeriod.value
        )
      });
    }
  };

  // show modal plans
  const handleClickSeePlans = (e) => {
    setOpenModalPlans(true);
  };

  // const findDefaultPromoCodeInPromoCodes = useMemo(() => {
  //   return allDefaultPromoCode.find((item) => promoCodes.includes(item));
  // }, [promoCodes, allDefaultPromoCode]);

  // get default promo code by plan
  useEffect(() => {
    if (isFreeTrial) return;
    if (!billableMetricsWithQuota) return;

    // const defaultPromoCodesPerPlan =
    //   promoCodesByPeriod
    //     .find(
    //       (plan) =>
    //         plan.planName ===
    //         getCurrentPlan(billableMetricsWithQuota, userPackagePlan.data)?.name
    //     )
    //     ?.promoCode.find(
    //       (promo: any) => promo.periodValue === selectedPeriod.value
    //     )?.code ?? '';

    // const promoCodeToUse = getUpdatedPromoCodesForPeriod(
    //   promoCodes,
    //   findDefaultPromoCodeInPromoCodes,
    //   defaultPromoCodesPerPlan
    // );

    // setPromoCodes(promoCodeToUse);
    setIsPromoError(false);
    // setIsPromoSubmitted(false);
    getPlanSimulationV2({
      variables: getPlanSimulationVariables(
        billableMetricsWithQuota,
        userPackagePlan.data,
        promoCodes,
        selectedPeriod.value
      )
    });
  }, [initCountryId, selectedPeriod, billableMetricsWithQuota, isFreeTrial]);

  const handleResetPromoCode = () => {
    setPromoCodeInput('');
    setIsPromoError(false);

    getPlanSimulationV2({
      variables: getPlanSimulationVariables(
        billableMetricsWithQuota,
        userPackagePlan.data,
        promoCodes,
        selectedPeriod.value
      )
    });
  };

  const handleRemovePromoCode = (codeToRemove: string) => {
    const updatedPromoCodes = promoCodes.filter(
      (code) => code !== codeToRemove
    );
    setPromoCodes(updatedPromoCodes);
    // setIsPromoError(false);
    // setIsPromoSubmitted(false);

    getPlanSimulationV2({
      variables: getPlanSimulationVariables(
        billableMetricsWithQuota,
        userPackagePlan.data,
        updatedPromoCodes,
        selectedPeriod.value
      )
    });
  };

  const calculatePeriodEnd = (startDate: Date, periodValue: number): Date => {
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + periodValue);
    return endDate;
  };

  const handleChangePackagePlan = (
    plan: UserPackagePlan,
    periodValue: string
  ) => {
    const dataWithQuota: BillableMetricsWithQuota[] =
      billableMetrics.billing_getBillableMetrics.map((metric) => {
        if (getQuota(metric.code, plan) !== null) {
          return {
            ...metric,
            quota: getQuota(metric.code, plan)
          };
        }
      });
    const filteredDataWithQuota = dataWithQuota.filter((item) => !!item);

    setbillableMetricsWithQuota(filteredDataWithQuota);
    setOpenModalPlans(false);
    handleChangePeriod(periodValue);
  };

  const getPriceCut = () => {
    return promoType === 'package'
      ? separateNumberFormat(discount)
      : separateNumberFormat(
          discount + referralDiscount > 0 ? discount + referralDiscount : 0
        );
  };

  const handleChangeQuota = (code: string, type: 'increment' | 'decrement') => {
    const updatedState = billableMetricsWithQuota.map((metric) => {
      if (metric.code !== code) return metric;
      return {
        ...metric,
        quota: type === 'increment' ? metric.quota + 1 : metric.quota - 1
      };
    });
    setbillableMetricsWithQuota(updatedState);
  };

  const handleChangeQuotaInput = (value: number, code: string) => {
    const updatedState = billableMetricsWithQuota.map((metric) => {
      if (metric.code !== code) return metric;
      return {
        ...metric,
        quota: value
      };
    });
    setbillableMetricsWithQuota(updatedState);
  };

  const handleDisableDecreaseItem = (metric: BillableMetricsWithQuota) => {
    if (!userPackagePlan) return false;
    const getStarterPlan = userPackagePlan.data.find(
      (item) => item.name === 'starter'
    );
    if (metric.code === 'SOCIAL_PROFILE')
      return metric.quota <= getStarterPlan.basicObjectQuota;
    if (metric.code === 'KEYWORD_HASHTAG')
      return metric.quota <= getStarterPlan.advanceObjectQuota;
    if (metric.code === 'CLIPPING')
      return metric.quota <= getStarterPlan.onlineMediaObjectQuota;
    return false;
  };

  // update package plan id according to quota match
  useEffect(() => {
    dispatch(
      reducerUpdatePackagePlan(
        getCurrentPlan(billableMetricsWithQuota, userPackagePlan.data)
      )
    );
  }, [billableMetricsWithQuota]);

  useEffect(() => {
    getUserPackagePlan().then(() => {
      getBillableMetrics();
    });
  }, []);

  return (
    <Paper
      sx={{
        width: theme.spacing(49),
        padding: 4,
        borderRadius: '12px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <LiteTypography variant="h1">
          {isFreeTrial
            ? 'Free Trial'
            : billableMetricsWithQuota
            ? getCurrentPlan(billableMetricsWithQuota, userPackagePlan.data)
                ?.displayName
            : planParam}
        </LiteTypography>
        <Button variant="text" size="small" onClick={handleClickSeePlans}>
          See Plans &gt;
        </Button>
      </Box>
      {!isFreeTrial && (
        <Box
          sx={{
            height: theme.spacing(5),
            width: '100%',
            background: '#F5F5F5',
            borderRadius: '100px',
            mt: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Grid container>
            {periodes.map(({ id, label, isActive }) => {
              if (isActive)
                return (
                  <Grid item sm={4} key={id}>
                    <ButtonPeriod variant="contained" fullWidth>
                      {label}
                    </ButtonPeriod>
                  </Grid>
                );
              return (
                <Grid item sm={4} key={id}>
                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => handleChangePeriod(id)}
                  >
                    {label}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
      {billableMetricsLoading ? (
        <Box
          height="300px"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={50} />
        </Box>
      ) : null}
      {billableMetricsWithQuota ? (
        <>
          {billableMetricsWithQuota
            .filter((metric) => metric.code !== 'ENTITY')
            .map((metric, index, array) => (
              <Box key={metric.id}>
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {metric.name}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: theme.spacing(16)
                    }}
                  >
                    <ButtonIcon
                      aria-label="minus"
                      size="small"
                      onClick={() =>
                        handleChangeQuota(metric.code, 'decrement')
                      }
                      disabled={
                        handleDisableDecreaseItem(metric) || isFreeTrial
                      }
                    >
                      <Remove
                        sx={{
                          background: theme.palette.common.white,
                          borderRadius: '50%',
                          color:
                            isFreeTrial || handleDisableDecreaseItem(metric)
                              ? '#D9D9D9'
                              : '#47A3E6',
                          fontSize: theme.spacing(4)
                        }}
                      />
                    </ButtonIcon>
                    {/* <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {metric.quota}
                    </Typography> */}
                    <OutlinedInput
                      onChange={(event) =>
                        handleChangeQuotaInput(
                          Number(event.target.value),
                          metric.code
                        )
                      }
                      value={metric.quota}
                      type="number"
                      inputProps={{ min: 0 }}
                      sx={{
                        width: '60px',
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                          {
                            WebkitAppearance: 'none',
                            margin: 0
                          },
                        '& input[type=number]': {
                          MozAppearance: 'textfield'
                        },
                        '& input': {
                          textAlign: 'center'
                        }
                      }}
                    />
                    <ButtonIcon
                      aria-label="plus"
                      size="small"
                      onClick={() =>
                        handleChangeQuota(metric.code, 'increment')
                      }
                      disabled={isFreeTrial}
                    >
                      <Add
                        sx={{
                          background: theme.palette.common.white,
                          borderRadius: '50%',
                          color: isFreeTrial ? '#D9D9D9' : '#47A3E6',
                          fontSize: theme.spacing(4)
                        }}
                      />
                    </ButtonIcon>
                  </Box>
                </Box>
                <Divider
                  sx={{
                    my: theme.spacing(2)
                  }}
                />
              </Box>
            ))}
        </>
      ) : null}
      <Stack direction="column" gap={2}>
        <FormControl
          error={isPromoError && !planSimulationLoading}
          fullWidth
          size="medium"
        >
          <FormLabel>Promo Code</FormLabel>
          <Stack direction="row" gap={0} width="100%">
            <OutlinedInput
              name="promoCode"
              onChange={(e) => {
                setPromoCodeInput(e.target.value);
                // if (isPromoSubmitted) {
                //   setIsPromoSubmitted(false);
                // }
              }}
              value={promoCodeInput}
              disabled={isFreeTrial || planSimulationLoading}
              sx={{
                flexGrow: 1,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear input"
                    onClick={handleResetPromoCode}
                    edge="end"
                    disabled={isFreeTrial}
                  >
                    <Cancel fontSize="small" sx={{ color: '#D9D9D9' }} />
                  </IconButton>
                </InputAdornment>
              }
            />
            <Button
              variant="contained"
              disabled={
                billableMetricsLoading ||
                !promoCodeInput.length ||
                isFreeTrial ||
                planSimulationLoading
              }
              sx={{
                borderRadius: 1,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0
              }}
              onClick={() => handleUsePromo(promoCodeInput)}
            >
              Use
            </Button>
          </Stack>
          <FormHelperText id="promo-code-error-text">
            {isPromoError && !planSimulationLoading
              ? promoCodeErrorMessage
              : null}
          </FormHelperText>
          {promoCodes.length > 0 && (
            <Stack
              direction="row"
              gap={1}
              sx={{
                mt: 2,
                flexWrap: 'nowrap',
                overflow: 'auto',
                scrollbarWidth: 'thin'
              }}
            >
              {promoCodes.map(
                (code) =>
                  // code !== findDefaultPromoCodeInPromoCodes &&
                  !isFreeTrial && (
                    <Chip
                      key={code}
                      label={code}
                      onDelete={() => handleRemovePromoCode(code)}
                      disabled={isFreeTrial}
                      sx={{
                        backgroundColor: '#E7F3F7',
                        '& .MuiChip-deleteIcon': {
                          color: themeColors.gray,
                          '&:hover': {
                            opacity: 0.7
                          }
                        }
                      }}
                    />
                  )
              )}
            </Stack>
          )}
        </FormControl>
        {discount + referralDiscount > 0 ? (
          <Stack direction="row" gap={1}>
            <Typography
              variant="body1"
              fontWeight={600}
              color="#9D9D9C"
              sx={{ textDecoration: 'line-through' }}
            >
              {`Rp${separateNumberFormat(subTotal ?? 0)}`}
              {'/'}
              {selectedPeriod.period}
            </Typography>
            <span
              style={{
                color: '#47A3E6',
                textDecorationLine: 'none',
                fontWeight: 600
              }}
            >
              (
              {promoType === 'package'
                ? separateNumberFormat(discount)
                : separateNumberFormat(
                    discount + referralDiscount > 0
                      ? discount + referralDiscount
                      : 0
                  )}{' '}
              off)
            </span>
          </Stack>
        ) : null}
        <Box>
          <Typography variant="body2">Grand Total</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center'
            }}
          >
            <Typography variant="h2">{`Rp${separateNumberFormat(
              total ?? 0
            )}`}</Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                ml: theme.spacing(0.5),
                transform: 'translateY(4px)'
              }}
            >{` / ${selectedPeriod.period}`}</Typography>
          </Box>
        </Box>
      </Stack>
      {openModalPlans ? (
        <ModalPlans
          open={openModalPlans}
          handleClose={() => setOpenModalPlans(false)}
          handleClickPlan={handleChangePackagePlan}
          isFreeTrial={isFreeTrial}
        />
      ) : null}
    </Paper>
  );
};

export default PricingCalculator;
